/**
 * EmptyState Component
 *
 * Generic empty state display with optional action button.
 * Used when there is no data to display.
 *
 * @module domains/personnel/presentation/components/shared/EmptyState
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';

/**
 * Props for EmptyState component
 */
export interface EmptyStateProps {
  /** Context key for specific empty messages (e.g., 'roles', 'employees') */
  context?: 'roles' | 'employees';
  /** Callback when action button is clicked */
  onAction?: () => void;
  /** Whether to show the action button (default: true) */
  showAction?: boolean;
}

const styles = {
  wrapper: 'flex items-center justify-center py-12',
  content: 'text-center flex flex-col items-center gap-2',
  title: 'text-sm text-muted-foreground',
  description: 'text-xs text-muted-foreground mt-1',
};

/**
 * EmptyState Component
 *
 * Displays an empty state message with optional action button.
 * Supports context-specific messages for roles and employees.
 *
 * @param {EmptyStateProps} props - Component properties
 * @returns {React.ReactElement} Empty state component
 *
 * @example
 * <EmptyState context="roles" onAction={() => history.push('/roles/create')} />
 */
export const EmptyState = ({ context, onAction, showAction = true }: EmptyStateProps): React.ReactElement => {
  const { t } = useTranslation('personnel');

  const title = context ? t(`emptyState.${context}.title`) : t('emptyState.title');
  const description = context ? t(`emptyState.${context}.description`) : t('emptyState.description');
  const actionLabel = context ? t(`emptyState.${context}.action`) : t('common.createButton');

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        <p className={styles.description}>{description}</p>
        {showAction && onAction && <Button onClick={onAction}>{actionLabel}</Button>}
      </div>
    </div>
  );
};
